import { deDE as clerkDeDE, enUS as clerkEnUS, frFR as clerkFrFR } from '@clerk/localizations';

/**
 * Clerk's sign-in copy, adjusted in three ways:
 *
 * - `{{applicationName}}` is pinned to "Plato". Clerk interpolates the instance
 *   name, which reads "Plato Republic Staging" outside production. The instance
 *   keeps that name so its transactional emails still say which environment
 *   sent them — only the UI is pinned.
 * - The code screen warns that the mail can be slow.
 * - German is de-formalised: Clerk ships "Sie", Plato says "du".
 *
 * Keys are spread level by level: Clerk merges a partial resource against its
 * English default, not against the language's own resource.
 */

/** Localization resources are plain strings, so a JSON round-trip is safe. */
function pinApplicationName(resource: typeof clerkDeDE): typeof clerkDeDE {
    const pinned: unknown = JSON.parse(JSON.stringify(resource).replaceAll('{{applicationName}}', 'Plato'));
    return pinned as typeof clerkDeDE;
}

const pinnedDe = pinApplicationName(clerkDeDE);
const pinnedEn = pinApplicationName(clerkEnUS);
const pinnedFr = pinApplicationName(clerkFrFR);

const codeDelayNoticeDe = 'Der Code kann manchmal ein paar Minuten brauchen.';
const codeDelayNoticeEn = 'The code can sometimes take a few minutes to arrive.';
const codeDelayNoticeFr = 'Le code peut parfois mettre quelques minutes à arriver.';

export const de = {
    ...pinnedDe,
    footerActionLink__useAnotherMethod: 'Andere Methode verwenden',
    signIn: {
        ...pinnedDe.signIn,
        start: {
            ...pinnedDe.signIn?.start,
            subtitleCombined: 'Willkommen zurück! Bitte melde dich an, um fortzufahren',
        },
        emailCode: {
            ...pinnedDe.signIn?.emailCode,
            title: 'Überprüfe deinen Posteingang',
            subtitle: codeDelayNoticeDe,
        },
        emailCodeMfa: {
            ...pinnedDe.signIn?.emailCodeMfa,
            title: 'Überprüfe deinen Posteingang',
            formTitle: 'Bestätigungscode',
            subtitle: codeDelayNoticeDe,
        },
        phoneCode: {
            ...pinnedDe.signIn?.phoneCode,
            title: 'Schau auf dein Telefon',
            subtitle: codeDelayNoticeDe,
        },
        phoneCodeMfa: {
            ...pinnedDe.signIn?.phoneCodeMfa,
            title: 'Schau auf dein Telefon',
            subtitle: codeDelayNoticeDe,
        },
        password: {
            ...pinnedDe.signIn?.password,
            title: 'Gib dein Passwort ein',
            actionLink: 'Andere Methode verwenden',
        },
        totpMfa: {
            ...pinnedDe.signIn?.totpMfa,
            subtitle: 'Gib den Code ein, den deine Authenticator-App erzeugt hat.',
        },
        backupCodeMfa: {
            ...pinnedDe.signIn?.backupCodeMfa,
            title: 'Gib einen Wiederherstellungscode ein',
        },
        alternativeMethods: {
            ...pinnedDe.signIn?.alternativeMethods,
            title: 'Andere Methode verwenden',
            subtitle: 'Probleme? Du kannst dich mit einer dieser Methoden anmelden.',
            actionText: 'Hast du keine davon?',
            blockButton__backupCode: 'Wiederherstellungscode verwenden',
            blockButton__passkey: 'Mit deinem Passkey anmelden',
            blockButton__password: 'Mit deinem Passwort anmelden',
            blockButton__totp: 'Authentifizierungs-App verwenden',
            getHelp: {
                ...pinnedDe.signIn?.alternativeMethods?.getHelp,
                content:
                    'Wenn du dich nicht anmelden kannst, schreib uns eine E-Mail. Wir stellen den Zugriff so schnell wie möglich wieder her.',
            },
        },
        newDeviceVerificationNotice:
            'Du meldest dich von einem neuen Gerät an. Zur Sicherheit deines Kontos bitten wir um eine Überprüfung.',
    },
    unstable__errors: {
        ...pinnedDe.unstable__errors,
        captcha_invalid:
            'Die Sicherheitsüberprüfung ist fehlgeschlagen. Bitte versuch es noch einmal oder melde dich beim Support.',
        form_code_incorrect: 'Der Code ist falsch. Bitte prüf ihn und versuch es noch einmal.',
        form_param_format_invalid__email_address: 'Bitte gib eine gültige E-Mail-Adresse ein.',
        form_param_nil: 'Ein Pflichtfeld ist leer. Bitte prüf deine Eingaben.',
        form_param_type_invalid__email_address: 'Bitte gib eine gültige E-Mail-Adresse ein.',
        form_param_type_invalid__phone_number: 'Bitte gib eine gültige Telefonnummer ein.',
        form_password_or_identifier_incorrect:
            'Passwort oder E-Mail-Adresse ist falsch. Versuch es noch einmal oder nutze eine andere Methode.',
        not_allowed_access:
            'Diese E-Mail-Adresse ist für die Anmeldung nicht freigegeben. Wenn das ein Fehler ist, melde dich bitte beim Support.',
        session_exists: 'Du bist bereits angemeldet.',
    },
} satisfies typeof clerkDeDE;

export const en = {
    ...pinnedEn,
    signIn: {
        ...pinnedEn.signIn,
        start: {
            ...pinnedEn.signIn?.start,
            subtitleCombined: 'Welcome back! Please sign in to continue',
        },
        emailCode: { ...pinnedEn.signIn?.emailCode, subtitle: codeDelayNoticeEn },
        emailCodeMfa: { ...pinnedEn.signIn?.emailCodeMfa, subtitle: codeDelayNoticeEn },
        phoneCode: { ...pinnedEn.signIn?.phoneCode, subtitle: codeDelayNoticeEn },
        phoneCodeMfa: { ...pinnedEn.signIn?.phoneCodeMfa, subtitle: codeDelayNoticeEn },
    },
} satisfies typeof clerkEnUS;

export const fr = {
    ...pinnedFr,
    signIn: {
        ...pinnedFr.signIn,
        start: {
            ...pinnedFr.signIn?.start,
            subtitleCombined: 'Bon retour ! Connectez-vous pour continuer',
        },
        emailCode: { ...pinnedFr.signIn?.emailCode, subtitle: codeDelayNoticeFr },
        emailCodeMfa: { ...pinnedFr.signIn?.emailCodeMfa, subtitle: codeDelayNoticeFr },
        phoneCode: { ...pinnedFr.signIn?.phoneCode, subtitle: codeDelayNoticeFr },
        phoneCodeMfa: { ...pinnedFr.signIn?.phoneCodeMfa, subtitle: codeDelayNoticeFr },
    },
} satisfies typeof clerkFrFR;
